import { Component, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import { DateRange } from '@/core/models/date-range';
import { AngularTranslations } from '@/core/types/angular-primeng-api';
import { DatePicker } from 'primeng/datepicker';
import { type } from 'arktype';
import { DataUtils } from '@/core/utils/data-utils';

const ranges = [
    'today',
    'yesterday',
    'thisWeek',
    'lastWeek',
    'thisMonth',
    'lastMonth',
    'thisQuarter',
    'lastQuarter',
    'thisYear',
    'lastYear',
    'last30days',
    'last12months',
] as const;

export const quickSelectRangeType = type.enumerated(...ranges);

// Note: The following type def is equivalent ot using ArkTypes infer op, but due to pure TS a bit faster:
export type QuickSelectRange = (typeof ranges)[number]; // same as 'today' | 'yesterday' | ...

type QuickSelectButton = {
    id: QuickSelectRange;
    label: string; // i18n key
    dateRange: () => DateRange;
};

export type DateRangeChangedEvent = {
    dateRange: DateRange; // TODO check if this can be undefined...
    dateRangeOption: QuickSelectRange | undefined;
};

@Component({
    selector: 'app-date-range-selector',
    templateUrl: './date-range-selector.component.html',
    standalone: false,
})
export class DateRangeSelectorComponent implements OnInit, OnChanges {
    @Input() licenseStatePro: boolean = false;
    // Export handlers provided by the parent
    @Input() userDateFormat?: string;
    @Input() firstDayOfWeek: number = 0;
    @Output() onDateChanged = new EventEmitter<DateRangeChangedEvent>();
    @Input() dateRange?: DateRange;
    @Input() dateRangeOption: QuickSelectRange | undefined = undefined;
    private dateRangeShadow?: DateRange;
    calendarLabel: string = '';
    @ViewChild('datePicker') datePicker!: DatePicker;
    quickSelectButtons: QuickSelectButton[];

    constructor(private readonly translate: TranslateService) {
        this.quickSelectButtons = this.getQuickSelectButtons();
    }

    public ngOnInit(): void {
        if (this.dateRangeOption) {
            const option = this.quickSelectButtons.find((b) => b.id === this.dateRangeOption);
            if (option) {
                this.dateRange = option.dateRange();
            }
        }
        this.dateRangeShadow = this.dateRange;
        this.callOnDateChanged(this.dateRangeOption);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['dateRangeOption'] && this.dateRangeOption) {
            this.selectDateRange(this.dateRangeOption);
        }
    }

    private getQuickSelectButtons(): QuickSelectButton[] {
        const buttons: QuickSelectButton[] = [];
        const addBtn = function (id: QuickSelectRange, label: string, dateRange: () => DateRange): void {
            buttons.push({ id, label, dateRange });
        };

        addBtn('today', 'standard_today', () => DateRangeHelper.getToday());
        addBtn('yesterday', 'standard_yesterday', () => DateRangeHelper.getYesterday());
        addBtn('thisWeek', 'standard_thisweek', () => DateRangeHelper.getThisWeek(this.firstDayOfWeek as 0 | 1));
        addBtn('lastWeek', 'standard_lastweek', () => DateRangeHelper.getLastWeek(this.firstDayOfWeek as 0 | 1));
        addBtn('thisMonth', 'standard_thismonth', () => DateRangeHelper.getThisMonth());
        addBtn('lastMonth', 'standard_lastmonth', () => DateRangeHelper.getLastMonth());
        addBtn('thisQuarter', 'standard_thisquarter', () => DateRangeHelper.getThisQuarter());
        addBtn('lastQuarter', 'standard_lastquarter', () => DateRangeHelper.getLastQuarter());
        addBtn('thisYear', 'standard_thisyear', () => DateRangeHelper.getThisYear());
        addBtn('lastYear', 'standard_lastyear', () => DateRangeHelper.getLastYear());
        addBtn('last30days', 'calendar_last_thirty_days', () => DateRangeHelper.getLast30Days());
        addBtn('last12months', 'calendar_last_twelve_months', () => DateRangeHelper.getLast12Months());

        return buttons;
    }

    public onClickOutside(): void {
        if (this.dateRangeShadow === this.dateRange) {
            return;
        }
        if (!DateRangeHelper.isValid(this.dateRange) && DateRangeHelper.isValid(this.dateRangeShadow)) {
            this.dateRange = this.dateRangeShadow;
            return;
        }
        this.callOnDateChanged();
    }

    public onSelect(): void {
        if (DateRangeHelper.isValid(this.dateRange)) {
            this.callOnDateChanged();
        }
    }

    public onClose(): void {
        // Note: hideOnDateTimeSelect="false" seems not to be working. Therefore, we have to manually reopen the overlay.
        if (!DateRangeHelper.isValid(this.dateRange)) {
            if (!this.datePicker.overlayVisible) {
                this.datePicker.showOverlay();
            }
        }
    }

    public onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.onEnterFromDatepicker(event);
        } else {
            if (!this.datePicker.overlayVisible) {
                this.datePicker.showOverlay();
            }
        }
    }

    private onEnterFromDatepicker(event: Event): void {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (DateRangeHelper.isValid(this.dateRange)) {
            this.callOnDateChanged();
        }
    }

    private isChanged(): boolean {
        const dateRangeChaned = !DataUtils.isEqual(this.dateRangeShadow, this.dateRange);
        // const dateRangeOptionChanged = this.dateRangeOption !== undefined && this.dateRangeOption !== this.dateRangeOption;
        return dateRangeChaned;
    }

    private callOnDateChanged(quickSelectId: QuickSelectRange | undefined = undefined): void {
        // check if the date range is actually changed, otherwise a change event should not be emitted.
        // this prevents duplicate emits (TODO needs further analysis why callOnDateChanged() is called twice)
        const isChanged = this.isChanged();

        if (this.datePicker && this.datePicker.overlayVisible) {
            this.datePicker.hideOverlay();
        }

        // if the range did not change and no button was pressed, do not change anything
        if (!isChanged && !quickSelectId) {
            return;
        }

        this.dateRangeShadow = this.dateRange;
        this.dateRangeOption = quickSelectId;

        if (quickSelectId === undefined) {
            this.calendarLabel = '';
        } else {
            const btn = this.quickSelectButtons.find((b) => b.id === quickSelectId);
            if (!btn) {
                this.calendarLabel = '';
            } else {
                this.translate.get([btn.label]).subscribe((translations: AngularTranslations) => {
                    this.calendarLabel = translations[btn.label];
                });
            }
        }

        if (this.onDateChanged && isChanged) {
            this.onDateChanged.emit({
                dateRange: this.dateRange!,
                dateRangeOption: this.dateRangeOption,
            });
        }
    }

    public selectDateRange(btnId: QuickSelectRange): void {
        const btn = this.quickSelectButtons.find((b) => b.id === btnId);
        if (!btn) {
            console.error(`DateRangeSelectorComponent - unexpected error: The button with id ${btnId} was not found.`);
            return;
        }
        this.dateRange = btn.dateRange();
        this.callOnDateChanged(btn.id);
    }
}
